import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Download, Bell, X, Smartphone } from 'lucide-react';
import { toast } from 'sonner';
import { registerServiceWorker, requestNotificationPermission, subscribeToPush } from '../utils/pwa';

export default function PWAInstallPrompt() {
  const [showInstallPrompt, setShowInstallPrompt] = useState(false);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);
  const [showManualPrompt, setShowManualPrompt] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  
  useEffect(() => {
    // Check if already installed
    if (window.matchMedia('(display-mode: standalone)').matches) {
      setIsInstalled(true);
      checkNotificationStatus();
      return;
    }
    
    // Show manual prompt after a delay if browser prompt doesn't fire
    const manualPromptTimeout = setTimeout(() => {
      if (!deferredPrompt && !isInstalled) {
        setShowManualPrompt(true);
      }
    }, 3000);
    
    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setShowInstallPrompt(true);
      setShowManualPrompt(false);
    };
    
    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    
    window.addEventListener('appinstalled', () => {
      console.log('[PWA] App installed successfully');
      setIsInstalled(true);
      setShowInstallPrompt(false);
      setShowManualPrompt(false);
      toast.success('App installed! Enable notifications for booking alerts.');
      setTimeout(() => {
        checkNotificationStatus();
      }, 2000);
    });
    
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      clearTimeout(manualPromptTimeout);
    };
  }, [deferredPrompt, isInstalled]);
  
  const checkNotificationStatus = async () => {
    if ('Notification' in window) {
      const permission = Notification.permission;
      if (permission === 'granted') {
        setNotificationsEnabled(true);
      } else if (permission === 'default') {
        setShowNotificationPrompt(true);
      }
    }
  };
  
  const handleInstall = async () => {
    if (!deferredPrompt) return;
    
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('[PWA] User accepted install');
    }
    
    setDeferredPrompt(null);
    setShowInstallPrompt(false);
  };
  
  const handleEnableNotifications = async () => {
    try {
      const registration = await registerServiceWorker();
      if (!registration) {
        toast.error('Service worker registration failed');
        return;
      }
      
      const granted = await requestNotificationPermission();
      if (!granted) {
        toast.error('Notification permission denied');
        return;
      }
      
      await subscribeToPush(registration);
      
      setNotificationsEnabled(true);
      setShowNotificationPrompt(false);
      toast.success('Notifications enabled! You will receive alerts for new bookings.');
    } catch (error) {
      console.error('[PWA] Notification setup failed:', error);
      toast.error('Failed to enable notifications');
    }
  };
  
  return (
    <>
      <AnimatePresence>
        {showInstallPrompt && !isInstalled && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-4 right-4 z-50 max-w-sm"
          >
            <div className="bg-white rounded-2xl shadow-2xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#5AC5C1' }}>
                    <Download className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold" style={{ color: '#2F5F5F' }}>Install Fur & Flair Admin</h4>
                    <p className="text-sm" style={{ color: '#6B7280' }}>Quick access from home screen</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowInstallPrompt(false)}
                  className="p-1 rounded-full hover:bg-gray-100"
                >
                  <X className="w-5 h-5" style={{ color: '#6B7280' }} />
                </button>
              </div>
              <button
                onClick={handleInstall}
                data-testid="install-app-button"
                className="w-full px-6 py-3 rounded-full font-bold transition-all hover:shadow-md"
                style={{ backgroundColor: '#5AC5C1', color: 'white' }}
              >
                Install App
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      <AnimatePresence>
        {showNotificationPrompt && !notificationsEnabled && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-4 right-4 z-50 max-w-sm"
          >
            <div className="bg-white rounded-2xl shadow-2xl p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#B8E5D2' }}>
                    <Bell className="w-6 h-6" style={{ color: '#2F5F5F' }} />
                  </div>
                  <div>
                    <h4 className="font-bold" style={{ color: '#2F5F5F' }}>Get Booking Alerts</h4>
                    <p className="text-sm" style={{ color: '#6B7280' }}>Instant notifications for new enquiries</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowNotificationPrompt(false)}
                  className="p-1 rounded-full hover:bg-gray-100"
                >
                  <X className="w-5 h-5" style={{ color: '#6B7280' }} />
                </button>
              </div>
              <button
                onClick={handleEnableNotifications}
                data-testid="enable-notifications-button"
                className="w-full px-6 py-3 rounded-full font-bold transition-all hover:shadow-md"
                style={{ backgroundColor: '#B8E5D2', color: '#2F5F5F' }}
              >
                Enable Notifications
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Manual install prompt - shows when browser prompt doesn't fire */}
      <AnimatePresence>
        {showManualPrompt && !isInstalled && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-4 right-4 z-50 max-w-sm"
          >
            <div className="bg-white rounded-2xl shadow-2xl p-6 border-2" style={{ borderColor: '#5AC5C1' }}>
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center" style={{ backgroundColor: '#5AC5C1' }}>
                    <Smartphone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold" style={{ color: '#2F5F5F' }}>Install Fur & Flair</h4>
                    <p className="text-sm" style={{ color: '#6B7280' }}>Add to home screen</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowManualPrompt(false)}
                  className="p-1 rounded-full hover:bg-gray-100"
                >
                  <X className="w-5 h-5" style={{ color: '#6B7280' }} />
                </button>
              </div>
              <div className="space-y-3 text-sm" style={{ color: '#6B7280' }}>
                <div className="flex items-start gap-2">
                  <span className="font-bold" style={{ color: '#2F5F5F' }}>Android:</span>
                  <span>Tap the menu (⋮) → "Install app" or "Add to Home screen"</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-bold" style={{ color: '#2F5F5F' }}>iPhone:</span>
                  <span>Tap Share → "Add to Home Screen"</span>
                </div>
              </div>
              <button
                onClick={() => setShowManualPrompt(false)}
                data-testid="dismiss-install-prompt"
                className="w-full mt-4 px-6 py-2 rounded-full font-medium transition-all hover:bg-gray-100 border"
                style={{ borderColor: '#D1E5E3', color: '#6B7280' }}
              >
                Got it
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
